import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Box, Typography, Tabs, Tab, CircularProgress, Alert, Paper, Stack,
  Button, Tooltip, Divider, useTheme,
} from '@mui/material';
import config from '../../config';
import StorageIcon from '@mui/icons-material/Storage';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import StatusChip from '../common/StatusChip';
import { ConnectorLogo, typeLabel, PAUSE_REASON_LABEL } from './connectorMeta';
import WebhookGuide from './WebhookGuide';
import { getSource, testConnection } from '../../redux/actions/ingestion/sources';
import { listJobs } from '../../redux/actions/ingestion/jobs';
import { listExecutions } from '../../redux/actions/ingestion/executions';
import ConnectorJobs from './ConnectorJobs';
import ConnectorExecutions from './ConnectorExecutions';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';

const fmtDate = (d) => { try { return d ? format(new Date(d), 'MMM d, yyyy HH:mm') : '—'; } catch (_e) { return '—'; } };
const rel = (d) => { try { return d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : '—'; } catch (_e) { return '—'; } };
const num = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');

// Derive a single source-level status from the source + its jobs.
const deriveStatus = (source, jobs) => {
  if (!source) return { tone: 'neutral', label: 'Unknown' };
  if (source.status !== 'active') return { tone: 'neutral', label: 'Inactive' };
  const paused = jobs.filter((j) => j.status === 'paused');
  if (paused.length > 0) {
    return { tone: 'warning', label: jobs.every((j) => j.status === 'paused') ? 'Paused' : 'Partly paused',
      reason: PAUSE_REASON_LABEL(paused[0].pause_kind), detail: paused[0].pause_reason };
  }
  if (source.test_status === 'failed') return { tone: 'error', label: 'Failed', detail: source.test_error };
  if (source.test_status === 'success') return { tone: 'success', label: 'Connected' };
  return { tone: 'warning', label: 'Not tested' };
};

const Kpi = ({ label, value, sub }) => {
  const theme = useTheme();
  return (
    <Box sx={{ px: 2, py: 1.25, minWidth: 110, flex: '1 1 0' }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, color: theme.palette.text.primary }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{sub}</Typography>}
    </Box>
  );
};

const ConnectorDetailPage = ({
  currentSource, currentSourceStatistics, jobs, executions,
  isLoadingSource, isLoadingJobs, getSource, listJobs, listExecutions, testConnection,
}) => {
  const { sourceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  const connector = currentSource;
  const connectorJobs = useMemo(() => (Array.isArray(jobs) ? jobs.filter((j) => j.source_id === sourceId) : []), [jobs, sourceId]);
  const stats = currentSourceStatistics || {};

  useEffect(() => {
    if (sourceId) {
      getSource(sourceId);
      listJobs({ sourceId });
      listExecutions({ sourceId, perPage: 25 });
    }
  }, [getSource, listJobs, listExecutions, sourceId]);

  // Live (no polling): source/job/execution transitions refresh the source
  // header (status + KPIs), the jobs tab and the executions tab as ingestion
  // runs happen.
  useRealtimeRefresh(eventPrefix('ingestion.'), () => {
    if (!sourceId) return;
    getSource(sourceId);
    listJobs({ sourceId });
    listExecutions({ sourceId, perPage: 25 });
  });

  if (isLoadingSource && !connector) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }
  if (!connector) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', mt: 6 }}>
        <Alert severity="error">Data source not found. It may have been deleted.</Alert>
        <Button onClick={() => navigate('/datasources')} sx={{ mt: 2, textTransform: 'none' }}>Back to Data Sources</Button>
      </Box>
    );
  }

  const status = deriveStatus(connector, connectorJobs);
  const conn = connector.config?.connection || {};
  const connBits = [];
  if (conn.host) connBits.push(conn.host);
  if (conn.database) connBits.push(conn.database);
  if (conn.project_id) connBits.push(conn.project_id);
  if (conn.bucket) connBits.push(conn.bucket);
  if (conn.base_url) connBits.push(conn.base_url);
  const isWebhook = connector.connector_type === 'webhook';

  const card = { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.paper };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <PageBreadcrumbs items={[
          { text: 'Data Sources', link: '/datasources', icon: <StorageIcon /> },
          { text: connector.name || 'Source' },
        ]} />
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {connector.kind === 'event' ? (
            <Button size="small" variant="outlined" startIcon={<BoltIcon />} sx={{ textTransform: 'none' }}
              onClick={() => navigate('/signals')}>View events</Button>
          ) : (
            <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} sx={{ textTransform: 'none' }}
              onClick={() => testConnection(sourceId)}>Test</Button>
          )}
          <Button size="small" variant="outlined" startIcon={<SettingsIcon />} sx={{ textTransform: 'none' }}
            onClick={() => navigate(`/datasources/edit/${sourceId}`)}>Edit</Button>
          {connector.kind !== 'event' && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
              onClick={() => navigate(`/datasources/${sourceId}/jobs/add`)}>Add sync</Button>
          )}
        </Stack>
      </Stack>

      {/* Source card */}
      <Paper elevation={0} sx={{ ...card, p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ConnectorLogo type={connector.connector_type} size={48} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>{connector.name}</Typography>
              {status.detail
                ? <Tooltip title={status.detail} arrow><span><StatusChip tone={status.tone} label={status.label} /></span></Tooltip>
                : <StatusChip tone={status.tone} label={status.label} />}
              {status.reason && <Typography variant="caption" color="warning.main">{status.reason}</Typography>}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
              {typeLabel(connector.connector_type)}{connBits.length ? ` · ${connBits.join(' / ')}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">Created {fmtDate(connector.created_at)}</Typography>
          </Box>
        </Stack>

        {!isWebhook && (
          <>
            <Divider sx={{ my: 1.5 }} />
            {/* KPI strip */}
            <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ flexWrap: 'wrap' }}>
              <Kpi label="Jobs" value={num(stats.jobs?.total ?? connectorJobs.length)} sub={`${stats.jobs?.active ?? 0} active`} />
              <Kpi label="Executions" value={num(stats.executions?.total)} sub={`${stats.executions?.last_24_hours ?? 0} in 24h`} />
              <Kpi label="Success" value={stats.executions?.success_rate != null ? `${stats.executions.success_rate}%` : '—'} sub={`${stats.executions?.failed ?? 0} failed`} />
              <Kpi label="Rows ingested" value={num(stats.performance?.total_rows_processed)} sub={stats.performance?.avg_throughput_records_per_sec ? `${Math.round(stats.performance.avg_throughput_records_per_sec)}/s avg` : ''} />
              <Kpi label="Last run" value={rel(stats.activity?.last_execution)} />
            </Stack>
          </>
        )}
      </Paper>

      {/* Webhook source: receive URLs + curl examples (no sync jobs). */}
      {isWebhook ? (
        <WebhookGuide
          eventsUrl={`${config.API_URL}/ingestion/webhooks/${sourceId}/events`}
          signalUrl={`${config.API_URL}/ingestion/webhooks/${sourceId}/signal`}
          card={card}
          eventConfig={connector.config?.event_config}
        />
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
              <Tab label={`Sync jobs (${connectorJobs.length})`} sx={{ textTransform: 'none' }} />
              <Tab label="Activity" sx={{ textTransform: 'none' }} />
            </Tabs>
          </Box>
          {tab === 0 && <ConnectorJobs connector={connector} jobs={connectorJobs} isLoading={isLoadingJobs} sourceId={sourceId} />}
          {tab === 1 && <ConnectorExecutions connector={connector} />}
        </>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  currentSource: state.ingestion_sources?.currentSource || null,
  currentSourceStatistics: state.ingestion_sources?.currentSourceStatistics || null,
  jobs: state.ingestion_jobs?.jobs || [],
  executions: state.ingestion_executions?.executions || [],
  isLoadingSource: state.ingestion_sources?.isLoadingSource || false,
  isLoadingJobs: state.ingestion_jobs?.isLoading || false,
});

export default connect(mapStateToProps, { getSource, listJobs, listExecutions, testConnection })(ConnectorDetailPage);
